import { getFirestore, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SUBSCRIPTION_LEVELS = {
  FREE: 'free',
  BASIC: 'basic',
  PRO: 'pro'
};

const FEATURES = {
  BASIC_INVOICING: 'basicInvoicing',
  REPORTING: 'reporting',
  FORECASTING: 'forecasting',
  UNLIMITED_INVOICES: 'unlimitedInvoices',
  CUSTOM_BRANDING: 'customBranding'
};

// Feature matrix defining what features are available at each subscription level
const FEATURE_MATRIX = {
  [SUBSCRIPTION_LEVELS.FREE]: [
    FEATURES.BASIC_INVOICING
  ],
  [SUBSCRIPTION_LEVELS.BASIC]: [
    FEATURES.BASIC_INVOICING,
    FEATURES.REPORTING
  ],
  [SUBSCRIPTION_LEVELS.PRO]: [
    FEATURES.BASIC_INVOICING,
    FEATURES.REPORTING,
    FEATURES.FORECASTING,
    FEATURES.UNLIMITED_INVOICES,
    FEATURES.CUSTOMBRANDING
  ]
};

class SubscriptionService {
  constructor() {
    this.cache = new Map();
    this.pendingChecks = new Map();
    this.lastCallTimestamp = new Map(); // NEW: Track last call per user
    this.CACHE_DURATION = 60 * 1000; // 1 minute cache
    this.RATE_LIMIT_MS = 15 * 1000; // NEW: 15 second rate limit
    
    // CRITICAL FIX: Add request deduplication for cloud function calls
    this.pendingCloudFunctionCalls = new Map(); // Track pending cloud function calls
    this.customerCreationLocks = new Map(); // Prevent duplicate customer creation
  }

  // Add the missing method to check if emulator is enabled
  _isEmulatorEnabled() {
    // Check if we're connected to the emulator by looking at several indicators
    try {
      // More robust emulator detection
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      
      // Check if emulator is explicitly enabled via environment
      const emulatorExplicitlyEnabled = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';
      
      // If we're on localhost but emulator isn't explicitly enabled, default to production
      const shouldUseEmulator = emulatorExplicitlyEnabled || 
                               (isLocalhost && process.env.NODE_ENV === 'development');      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Emulator detection - localhost: ${isLocalhost}, explicit: ${emulatorExplicitlyEnabled}, result: ${shouldUseEmulator}`);
      }
      return shouldUseEmulator;    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Error detecting emulator mode:", error);
      }
      // Default to production if detection fails
      return false;
    }
  }
  // Add direct cache invalidation method
  invalidateCache(userId) {
    if (userId) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Explicitly invalidating subscription cache for user: ${userId}`);
      }
      this.cache.delete(userId);
      this.lastCallTimestamp.delete(userId); // Also clear rate limiting
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('Clearing all subscription caches');
      }
      this.cache.clear();
      this.lastCallTimestamp.clear();
    }
    
    // CRITICAL FIX: Also clear customer ID cache
    if (userId) {
      const userEmailKey = Object.keys(customerIdCache).find(key => key.startsWith(userId));
      if (userEmailKey) {
        customerIdCache.delete(userEmailKey);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Cleared customer ID cache for key: ${userEmailKey}`);
        }
      }
      customerCreationTimestamps.delete(userId);
    } else {
      customerIdCache.clear();
      customerCreationTimestamps.clear();
    }
  }

  // Add a method to completely reset user subscription data
  async resetUserSubscriptionData(userId, email) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[subscriptionService] Resetting all subscription data for user: ${userId}`);
    }
    
    // Clear all caches
    this.invalidateCache(userId);
    
    // Clear browser storage
    try {
      localStorage.removeItem(`subscription_${userId}`);
      localStorage.removeItem(`customer_${userId}`);
      sessionStorage.removeItem(`subscription_${userId}`);
      sessionStorage.removeItem(`customer_${userId}`);
    } catch (e) {
      // Storage might not be available
    }
    
    // Force a fresh check from cloud function
    return await this.getUserSubscription(userId, { 
      forceRefresh: true, 
      force: true,
      resetCache: true 
    });
  }  // Improved getUserSubscription method with better rate limiting
  async getUserSubscription(userId, options = {}) {
    if (!userId) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Missing user ID for subscription check');
      }
      return { status: 'inactive', level: 'free' };
    }

    // NEW: Enhanced rate limiting - CRITICAL FIX: Respect force flag
    const now = Date.now();
    const lastCall = this.lastCallTimestamp.get(userId);
      if (lastCall && (now - lastCall) < this.RATE_LIMIT_MS && !options.force && !options.forceRefresh) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Rate limited: Last call was ${((now - lastCall) / 1000).toFixed(1)}s ago`);
      }
      
      // Return cached data if available
      const cachedData = this.cache.get(userId);
      if (cachedData) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Returning cached data due to rate limit');
        }
        return cachedData.data;
      }
      
      // If no cache, return basic free tier to prevent blocking
      return {
        status: 'inactive',
        level: 'free',
        fromCache: true,
        source: 'rate_limited_fallback'
      };
    }

    try {      // If forceRefresh is specified, try cloud function first, then fallback
      if (options.forceRefresh) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Force refreshing subscription data - trying cloud function first');
        }
        
        // Update rate limit timestamp
        this.lastCallTimestamp.set(userId, now);
        
        // Get user email for the function call
        const userEmail = await this._getUserEmail(userId);
        
        // Try cloud function with short timeout first
        try {
          // CRITICAL FIX: Always use _doSubscriptionCheck for consistency
          const result = await Promise.race([
            this._doSubscriptionCheck(userId, userEmail, { forceCleanLog: true }), // Added forceCleanLog parameter
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Cloud function timeout')), 5000) // Short 5s timeout
            )
          ]);
          
          // If successful, cache and return
          this.cache.set(userId, {
            data: result,
            timestamp: now
          });
          
          return result;        } catch (functionError) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Cloud function failed, falling back to Firestore:', functionError.message);
          }
          
          // Fallback to Firestore data immediately
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists() && userDoc.data().subscription) {
              if (process.env.NODE_ENV === 'development') {
                console.log('Using Firestore subscription data as primary source');
              }
              const firestoreData = {
                ...userDoc.data().subscription,
                fromCache: true,
                error: 'cloud_function_unavailable',
                source: 'firestore_primary_fallback',
                synced: true // Mark as synced since we have valid data
              };
              
              // Cache the Firestore data
              this.cache.set(userId, {
                data: firestoreData,
                timestamp: now
              });
              
              return firestoreData;
            }
          } catch (firestoreError) {
            if (process.env.NODE_ENV === 'development') {
              console.error('Firestore fallback also failed:', firestoreError);
            }
          }
          
          // Final fallback to free tier
          return {
            status: 'inactive',
            level: 'free',
            error: 'all_sources_failed',
            source: 'final_fallback',
            fromCache: true,
            synced: true // Mark as synced to prevent infinite retries
          };
        }
      }
      
      // First check for an existing result in memory cache
      const cachedData = this.cache.get(userId);
      
      if (cachedData && (now - cachedData.timestamp < this.CACHE_DURATION)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Using cached subscription data from memory');
        }
        return cachedData.data;
      }

      // Check for a pending request to avoid multiple simultaneous calls
      const pendingCheck = this.pendingChecks.get(userId);
      if (pendingCheck) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Waiting for pending subscription check');
        }
        try {
          return await pendingCheck;
        } catch (error) {
          // If pending check failed, continue to try again
          this.pendingChecks.delete(userId);
        }
      }

      // Start a new check and store the promise
      if (process.env.NODE_ENV === 'development') {
        console.log('Starting new subscription check for', userId);
      }
      
      // Try Firestore first for non-force refresh
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists() && userDoc.data().subscription) {
          const firestoreData = {
            ...userDoc.data().subscription,
            fromCache: true,
            source: 'firestore_direct',
            synced: true
          };
          
          // Cache the data
          this.cache.set(userId, {
            data: firestoreData,
            timestamp: now
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Using Firestore data directly (no cloud function needed)');
          }
          return firestoreData;
        }
      } catch (firestoreError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Firestore check failed, will try cloud function:', firestoreError);
        }
      }
      
      // Update rate limit timestamp before calling function
      this.lastCallTimestamp.set(userId, now);
      
      // If Firestore doesn't have data, try cloud function
      const userEmail = await this._getUserEmail(userId);
      const checkPromise = this._doSubscriptionCheck(userId, userEmail); // Pass email here
      this.pendingChecks.set(userId, checkPromise);

      let result;
      try {
        result = await Promise.race([
          checkPromise, 
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Cloud function timeout')), 8000)
          )
        ]);
      } catch (error) {
        // Remove from pending checks
        this.pendingChecks.delete(userId);
        
        if (process.env.NODE_ENV === 'development') {
          console.error('Cloud function failed, returning free tier:', error.message);
        }
        
        // Return free tier with synced flag to prevent retries
        const fallbackData = {
          status: 'inactive',
          level: 'free',
          error: error.message,
          source: 'function_failed_fallback',
          fromCache: true,
          synced: true
        };
        
        // Cache the fallback
        this.cache.set(userId, {
          data: fallbackData,
          timestamp: now
        });
        
        return fallbackData;
      }
      
      // Update the cache with the new result
      this.cache.set(userId, {
        data: result,
        timestamp: now
      });
      
      // Remove from pending checks
      this.pendingChecks.delete(userId);

      return result;
    } catch (error) {
      console.error('Error getting user subscription:', error);
      this.pendingChecks.delete(userId);
      
      // Try to return cached data as final fallback
      const cachedData = this.cache.get(userId);
      if (cachedData) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Returning cached data due to error');
        }
        return {
          ...cachedData.data,
          error: error.message,
          source: 'error_fallback_cache'
        };
      }
      
      return { 
        status: 'inactive', 
        level: 'free',
        error: error.message,
        source: 'error_fallback_default',
        synced: true // Prevent infinite retries
      };
    }
  }

  // Add helper method to get user email
  async _getUserEmail(userId) {
    try {      // First try to get from current auth user
      const auth = getAuth();
      if (auth.currentUser && auth.currentUser.uid === userId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Using email from current auth user:', auth.currentUser.email);
        }
        return auth.currentUser.email;
      }
        // Fallback to Firestore
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists() && userDoc.data().email) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Using email from Firestore user document:', userDoc.data().email);
        }
        return userDoc.data().email;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.warn('No email found for user:', userId);
      }
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting user email:', error);
      }
      return null;
    }
  }

  /**
   * Check subscription status directly via Firebase Function
   */  async _doSubscriptionCheck(userId, email, options = {}) {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Performing direct subscription check for user:', userId, 'with email:', email);
      }
      
      // Validate email before making the call
      if (!email || !email.includes('@')) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Invalid or missing email for subscription check:', email);
        }
        throw new Error('Valid email required for subscription check');
      }

      // CRITICAL FIX: Prevent duplicate simultaneous calls
      const callKey = `${userId}_${email}`;
      
      // Check if there's already a pending call for this user
      if (this.pendingCloudFunctionCalls.has(callKey)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[subscriptionService] Waiting for existing cloud function call to complete:', callKey);
        }
        try {
          return await this.pendingCloudFunctionCalls.get(callKey);
        } catch (error) {
          // If the pending call failed, remove it and continue with a new call
          this.pendingCloudFunctionCalls.delete(callKey);
        }
      }

      // Create a new promise for this call
      const callPromise = this._makeCloudFunctionCall(userId, email, options);
      
      // Store the promise to prevent duplicate calls
      this.pendingCloudFunctionCalls.set(callKey, callPromise);
      
      try {
        const result = await callPromise;
        return result;
      } finally {
        // Clean up the pending call regardless of success or failure
        this.pendingCloudFunctionCalls.delete(callKey);
      }
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Unexpected error in _doSubscriptionCheck:', error);
      }
      
      // Always try Firestore fallback on any error
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists() && userDoc.data().subscription) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Using Firestore subscription data after function error');
          }
          return {
            ...userDoc.data().subscription,
            fromCache: true,
            source: 'firestore_after_function_error',
            error: error.message
          };
        }
      } catch (firestoreError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Firestore fallback after function error failed:', firestoreError);
        }
      }
      
      return { 
        status: 'inactive', 
        level: 'free',
        error: error.message,
        source: 'unexpected_error'
      };
    }
  }

  // CRITICAL FIX: Extract the actual cloud function call logic
  async _makeCloudFunctionCall(userId, email, options = {}) {
    // Check emulator status
    const isEmulator = this._isEmulatorEnabled();
    if (process.env.NODE_ENV === 'development') {
      console.log(isEmulator ? 'Attempting to use Firebase Functions emulator' : 'Using production Firebase Functions');
    }
    
    // Get existing subscription data from Firestore first
    let existingSubscriptionData = null;
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        existingSubscriptionData = userDoc.data().subscription || null;        
      }
    } catch (firestoreError) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error retrieving existing subscription data from Firestore:', firestoreError);
      }
    }

    // Call the cloud function (works for both emulator and production)
    const syncFunction = getFunctions();      
    const syncStripeCustomerTrigger = httpsCallable(syncFunction, 'syncStripeCustomer');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Calling syncStripeCustomer with data:', { userId, email });
    }
    
    const functionCall = syncStripeCustomerTrigger({ userId, email });
    const timeout = 8000; // 8 second timeout
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Function call timeout')), timeout)
    );
      
    const result = await Promise.race([functionCall, timeoutPromise]);

    // CRITICAL FIX: The cloud function returns data directly, not wrapped in { success: ... }
    if (process.env.NODE_ENV === 'development') {
      console.log('Cloud function raw result:', result);
      console.log('Cloud function result.data:', result.data);
    }
    
    if (result.data) {
      // The syncStripeCustomer function returns the subscription data directly
      const subscriptionData = result.data;        
      
      // CRITICAL FIX: Always use consistent logging to avoid confusion
      if (process.env.NODE_ENV === 'development') {
        console.log('DETAILED subscription data from cloud function:', {
          status: subscriptionData.status,
          level: subscriptionData.level,
          cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
          isPaid: subscriptionData.isPaid,
          currentPeriodEnd: subscriptionData.currentPeriodEnd
        });
      }
      
      // CRITICAL FIX: Ensure ALL fields are preserved in the processed data
      const processedData = {
        status: subscriptionData.status || 'inactive',
        level: subscriptionData.level || 'free',
        stripeCustomerId: subscriptionData.stripeCustomerId,
        stripeSubscriptionId: subscriptionData.stripeSubscriptionId,
        currentPeriodStart: subscriptionData.currentPeriodStart,
        currentPeriodEnd: subscriptionData.currentPeriodEnd,
        trialStart: subscriptionData.trialStart,
        trialEnd: subscriptionData.trialEnd,
        cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd || false,
        canceledAt: subscriptionData.canceledAt,
        endedAt: subscriptionData.endedAt,
        isFreeTrial: subscriptionData.isFreeTrial || false,
        isPaid: subscriptionData.isPaid || false,
        availedTrials: subscriptionData.availedTrials || (existingSubscriptionData?.availedTrials || {}),
        updatedAt: new Date().toISOString(),
        fromCache: false,
        synced: true,
        source: 'cloud_function_success'
      };        
      
      // CRITICAL FIX: Always use consistent logging
      if (process.env.NODE_ENV === 'development') {
        console.log('FINAL processed subscription data:', {
          status: processedData.status,
          cancelAtPeriodEnd: processedData.cancelAtPeriodEnd,
          isPaid: processedData.isPaid
        });
      }
      
      return processedData;
    }      
    
    // If we reach here, the function returned no data
    if (process.env.NODE_ENV === 'development') {
      console.warn('Cloud function returned no data');
    }
    
    // If we have existing data, return it
    if (existingSubscriptionData) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Function call returned empty/failed data, using existing Firestore data');
      }
      return {
        ...existingSubscriptionData,
        fromCache: true,
        source: 'firestore_fallback_empty_function_result'
      };
    }
    
    // Final fallback
    return { 
      status: 'inactive', 
      level: 'free',
      source: 'empty_function_result_no_existing_data'
    };
  }

  /**
   * Ensures the user has a Stripe customer ID, creating one if necessary.
   * @param {string} userId - The Firebase user ID
   * @param {string} email - The user's email address
   * @returns {Promise<object>} - Object containing the Stripe customer ID or error information
   */  async ensureStripeCustomer(userId, email) {
    if (!userId || !email) {
      if (process.env.NODE_ENV === 'development') {
        console.error("[subscriptionService] ensureStripeCustomer: userId and email are required.");
      }
      return {
        success: false,
        error: "User ID and email are required to create a customer.",
        skipError: false
      };
    }
    
    // CRITICAL FIX: Enhanced deduplication with locks
    const lockKey = `${userId}_${email}`;
    
    // Check if customer creation is already in progress
    if (this.customerCreationLocks.has(lockKey)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[subscriptionService] Customer creation already in progress for ${userId}, waiting...`);
      }
      try {
        return await this.customerCreationLocks.get(lockKey);
      } catch (error) {
        // If the locked operation failed, remove the lock and continue
        this.customerCreationLocks.delete(lockKey);
      }
    }

    // Create a promise for the customer creation process
    const creationPromise = this._createCustomerWithLock(userId, email, lockKey);
    
    // Store the promise to prevent duplicate calls
    this.customerCreationLocks.set(lockKey, creationPromise);
    
    try {
      return await creationPromise;
    } finally {
      // Always clean up the lock
      this.customerCreationLocks.delete(lockKey);
    }
  }

  // CRITICAL FIX: Extract customer creation logic with proper locking
  async _createCustomerWithLock(userId, email, lockKey) {
    // Check cache first to prevent duplicate creation attempts
    const cacheKey = `${userId}_${email}`;
    if (customerIdCache.has(cacheKey)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[subscriptionService] Using cached customer ID for ${userId}`);
      }
      return { 
        success: true,
        stripeCustomerId: customerIdCache.get(cacheKey),
        fromCache: true 
      };
    }
    
    // Enhanced cooldown check to prevent rapid successive calls
    const now = Date.now();
    const lastCreationAttempt = customerCreationTimestamps.get(userId) || 0;
    if (now - lastCreationAttempt < 10000) { // 10 second cooldown
      if (process.env.NODE_ENV === 'development') {
        console.log(`[subscriptionService] Customer creation on cooldown for user: ${userId}`);
      }
      return { 
        success: false, 
        error: 'Creation attempt too soon after previous attempt',
        retryAfter: 10 - Math.floor((now - lastCreationAttempt) / 1000),
        skipError: true
      };
    }
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Ensuring Stripe customer record exists for user: ${userId}`);
      }
      
      // Always check Firestore first for existing customer ID
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists() && userDoc.data().stripeCustomerId) {
          const existingCustomerId = userDoc.data().stripeCustomerId;
          if (process.env.NODE_ENV === 'development') {
            console.log(`[subscriptionService] Found existing stripeCustomerId in Firestore: ${existingCustomerId}`);
          }
          
          // Cache the existing customer ID
          customerIdCache.set(cacheKey, existingCustomerId);
          
          return { 
            success: true,
            stripeCustomerId: existingCustomerId,
            fromFirestore: true
          };
        }
      } catch (firestoreError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn("[subscriptionService] Error checking Firestore for existing customer ID:", firestoreError);
        }
      }
      
      // Set timestamp for this creation attempt
      customerCreationTimestamps.set(userId, now);
      
      // Call cloud function with explicit deduplication flag
      const functions = getFunctions();
      const ensureCustomer = httpsCallable(functions, 'ensureStripeCustomer');
      
      if (process.env.NODE_ENV === 'development') {
        console.log("[subscriptionService] Calling ensureStripeCustomer Cloud Function with:", { email, userId });
      }
      
      const result = await ensureCustomer({ 
        email: email,
        userId: userId,
        preventDuplicates: true // Add flag to cloud function
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[subscriptionService] Cloud Function result:`, result.data);
      }
      
      if (result.data && result.data.stripeCustomerId) {
        // Cache the new customer ID
        customerIdCache.set(cacheKey, result.data.stripeCustomerId);
        
        // Try to update Firestore with the new ID
        try {
          await updateDoc(doc(db, 'users', userId), {
            stripeCustomerId: result.data.stripeCustomerId,
            updatedAt: new Date().toISOString()
          });
          if (process.env.NODE_ENV === 'development') {
            console.log("[subscriptionService] Updated Firestore with new Stripe customer ID");
          }
        } catch (updateError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn("[subscriptionService] Could not update Firestore with Stripe customer ID:", updateError);
          }
        }
        
        return {
          success: true,
          stripeCustomerId: result.data.stripeCustomerId,
          fromCloudFunction: true
        };
      } else {
        return {
          success: false,
          error: "Invalid response from server",
          skipError: true
        };
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[subscriptionService] Error ensuring Stripe customer:', error);
      }
      
      // On error, try one more Firestore check in case it was created by another process
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists() && userDoc.data().stripeCustomerId) {
          const existingCustomerId = userDoc.data().stripeCustomerId;
          if (process.env.NODE_ENV === 'development') {
            console.log(`[subscriptionService] Found customer ID in Firestore after error: ${existingCustomerId}`);
          }
          
          customerIdCache.set(cacheKey, existingCustomerId);
          return { 
            success: true,
            stripeCustomerId: existingCustomerId,
            fromFirestoreAfterError: true
          };
        }
      } catch (firestoreError) {
        if (process.env.NODE_ENV === 'development') {
          console.error("[subscriptionService] Final Firestore check also failed:", firestoreError);
        }
      }
      
      return {
        success: false,
        error: error.message || "Unknown error",
        skipError: false
      };
    }
  }

  /**
   * Sync subscription data with Stripe
   * @param {string} userId - The Firebase user ID
   * @param {string} email - The user's email address
   * @returns {Promise<object>} - The synced subscription data
   */  async syncWithStripe(userId, email) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Performing subscription check for user:', userId);
    }
    try {
      // Ensure we have a valid email
      const userEmail = email || await this._getUserEmail(userId);
      
      if (!userEmail) {
        throw new Error('No email available for Stripe sync');
      }
      
      const functions = getFunctions();
      const syncStripeCustomer = httpsCallable(functions, 'syncStripeCustomer');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Syncing customer data with Stripe using email:', userEmail);
      }
      const result = await syncStripeCustomer({ 
        userId: userId,
        email: userEmail, // Use the resolved email
        forceRefresh: true
      });

      // CRITICAL FIX: If result is missing or inactive, always return a normalized inactive subscription
      if (!result?.data || result.data.status === 'inactive' || result.data.level === 'free') {
        return {
          status: 'inactive',
          level: 'free',
          availedTrials: {},
          stripeCustomerId: null,
          source: 'stripe_not_found'
        };
      }
      
      // CRITICAL FIX: Process data through _doSubscriptionCheck to ensure consistency
      if (result.data) {
        // Add in any missing fields that _doSubscriptionCheck would add
        result.data.updatedAt = new Date().toISOString();
        result.data.fromCache = false;
        result.data.synced = true;
        result.data.source = 'syncWithStripe';
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Stripe sync result:', result.data);
      }
      return result.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error syncing with Stripe:', error);
      }
      throw error;
    }
  }

  // CRITICAL FIX: Completely revised normalizeSubscriptionData to ALWAYS use priceId as source of truth
  normalizeSubscriptionData(data) {
    if (!data) return { status: 'inactive', level: 'free', availedTrials: {} };

    // Handle data from Stripe webhook or direct DB query
    const subscriptionData = data.subscription || data;
    
    // Normalize "trialing" to "active" for simplicity
    let status = subscriptionData.status || 'inactive';
    if (status === 'trialing') {
      status = 'active';
    }
    
    // CRITICAL FIX: ALWAYS determine level from priceId first
    let level = 'free';
    if (process.env.NODE_ENV !== 'production') {
      console.log('Raw subscription data for normalization:', {
        priceId: subscriptionData.priceId,
        level: subscriptionData.level,
        source: 'Stripe sync'
      });
    }
    
    if (subscriptionData.priceId) {
      level = this.getPlanFromPriceId(subscriptionData.priceId);
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Determined level from price ID: ${level}`);
      }
    } else if (subscriptionData.level) {
      level = subscriptionData.level;
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Using provided level field: ${level}`);
      }
    }
    
    // Get the basic subscription info with the correctly determined level
    const normalizedData = {
      status: status,
      level: level,
      currentPeriodEnd: subscriptionData.currentPeriodEnd || null,
      updatedAt: new Date().toISOString(),
      availedTrials: subscriptionData.availedTrials || {}
    };

    // CRITICAL FIX: Only add fields that are not undefined
    if (subscriptionData.priceId) {
      normalizedData.priceId = subscriptionData.priceId;
    }
    if (subscriptionData.stripeSubscriptionId) {
      normalizedData.stripeSubscriptionId = subscriptionData.stripeSubscriptionId;
    }
    if (subscriptionData.stripeCustomerId) {
      normalizedData.stripeCustomerId = subscriptionData.stripeCustomerId;
    }

    // Process the various payment and trial flags
    normalizedData.isPaid = !!subscriptionData.isPaid;
    normalizedData.isFreeTrial = !!subscriptionData.isFreeTrial;
    normalizedData.willBecomePaid = !!subscriptionData.willBecomePaid;
    normalizedData.hasPaymentMethod = !!subscriptionData.hasPaymentMethod;
    normalizedData.trialEnd = subscriptionData.trialEnd || null;
    normalizedData.willExpire = !!subscriptionData.willExpire;

    // If the explicit flags aren't provided, infer them
    if (!('isPaid' in subscriptionData) || !('isFreeTrial' in subscriptionData)) {
      const now = Math.floor(Date.now() / 1000);
      
      // Check if this is a trial that's still active
      if (subscriptionData.status === 'trialing' || 
          (subscriptionData.trialEnd && subscriptionData.trialEnd > now)) {
        normalizedData.isFreeTrial = true;
        normalizedData.isPaid = false;
      } 
      // Check if this was a trial that has become a paid subscription
      else if (subscriptionData.status === 'active' && subscriptionData.trialEnd) {
        normalizedData.isFreeTrial = true; // It began as a trial
        normalizedData.isPaid = true;      // But is now paid
      }
      // Regular active subscription
      else if (subscriptionData.status === 'active') {
        normalizedData.isPaid = true;
        normalizedData.isFreeTrial = false;
      }
    }
    
    // Determine the appropriate display end date
    if (normalizedData.isFreeTrial && !normalizedData.isPaid) {
      // Active trial - show trial end date
      normalizedData.displayEndDate = subscriptionData.trialEnd || subscriptionData.currentPeriodEnd;
    } else {
      // Paid subscription - show current period end as next billing date
      normalizedData.displayEndDate = subscriptionData.currentPeriodEnd;
    }

    return normalizedData;
  }
  
  // CRITICAL FIX: Make price ID mapping dynamic from environment variables
  getPlanFromPriceId(priceId) {
    if (!priceId) return 'free';
    
    // CRITICAL FIX: Get price IDs from environment variables instead of hardcoding
    const basicPriceId = process.env.REACT_APP_STRIPE_BASIC_PRICE_ID;
    const proPriceId = process.env.REACT_APP_STRIPE_PRO_PRICE_ID;
      // Add detailed debug logging to trace the mapping
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Mapping price ID: ${priceId}`);
      console.log(`Environment basic price ID: ${basicPriceId}`);
      console.log(`Environment pro price ID: ${proPriceId}`);
    }
    
    if (priceId === basicPriceId) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Matched BASIC plan from environment variable');
      }
      return 'basic';
    }
    
    if (priceId === proPriceId) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Matched PRO plan from environment variable');
      }
      return 'pro';
    }
    
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`Unknown price ID: ${priceId} - defaulting to free plan`);
    }
    return 'free';
  }

  async syncSubscriptionStatus(userId) {
    console.log('syncSubscriptionStatus called for', userId);

    // For tracking which component called this function
    console.trace('Subscription sync trace');
    
    if (!userId) {
      console.log('No userId provided to syncSubscriptionStatus');
      return { status: 'inactive', level: 'free' };
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      // First check if user document exists
      if (!userDoc.exists()) {
        console.log('No user document found, returning free tier');
        return { status: 'inactive', level: 'free' };
      }

      const userData = userDoc.data();
      console.log('User subscription data:', userData?.subscription || 'No subscription data');

      // Only proceed with Stripe check for PRO routes
      if (userData?.subscription?.status === 'active') {
        const functions = getFunctions();
        const checkStripe = httpsCallable(functions, 'checkStripeSubscription');
        
        console.log('Checking with Stripe API');
        const result = await checkStripe({ userId });
        console.log('Stripe API result:', result.data);

        if (result.data?.status === 'active') {
          const newData = {
            status: 'active',
            level: this.getPlanFromPriceId(result.data.priceId), // FIXED: Correct method name
            currentPeriodEnd: result.data.currentPeriodEnd
          };
          
          // Update cache
          this.subscriptionCache.set(userId, newData);
          return newData;
        }
      }

      // If we got here, either there's no subscription or it's not active
      const freeData = { status: 'inactive', level: 'free' };
      this.subscriptionCache.set(userId, freeData);
      return freeData;

    } catch (error) {
      console.error('Subscription sync error:', error);
      return { status: 'inactive', level: 'free' };
    }
  }

  async updateSubscriptionStatus(userId) {
    if (!userId) return null;

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();

      // Get fresh data from Stripe
      const functions = getFunctions();
      const checkStripe = httpsCallable(functions, 'checkStripeSubscription');
      
      const result = await checkStripe({ 
        userId,
        email: userData.email,
        forceRefresh: true
      });

      // Only update Firestore if we have valid data
      if (result.data?.status && result.data?.priceId) {
        const subscriptionData = {
          status: result.data.status,
          level: this.getPlanFromPriceId(result.data.priceId), // FIXED: Correct method name
          currentPeriodEnd: result.data.currentPeriodEnd || null,
          priceId: result.data.priceId,
          lastChecked: new Date().toISOString()
        };

        await updateDoc(doc(db, 'users', userId), {
          subscription: subscriptionData,
          stripeCustomerId: result.data.stripeCustomerId || null
        });

        return subscriptionData;
      }

      // If no Stripe data, return free tier status
      return {
        status: 'inactive',
        level: 'free',
        lastChecked: new Date().toISOString()
      };

    } catch (error) {
      console.error('Error updating subscription status:', error);
      throw new Error('No active subscription found');
    }
  }

  getPlanLevel(priceId) {
    console.log('Checking plan level for priceId:', priceId);
    
    // REMOVED: Use dynamic environment variables instead of hardcoded mapping
    const basicPriceId = process.env.REACT_APP_STRIPE_BASIC_PRICE_ID;
    const proPriceId = process.env.REACT_APP_STRIPE_PRO_PRICE_ID;
    
    if (priceId === basicPriceId) return SUBSCRIPTION_LEVELS.BASIC;
    if (priceId === proPriceId) return SUBSCRIPTION_LEVELS.PRO;
    
    console.log('Determined plan level: free (no match found)');
    return SUBSCRIPTION_LEVELS.FREE;
  }

  async hasFeatureAccess(userId, feature) {    // Ensure userId is provided
    if (!userId) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('hasFeatureAccess called without userId');
      }
      return false;
    }
    // Use getUserSubscription to get the latest subscription status
    const subscription = await this.getUserSubscription(userId); 
    // The subscription object from getUserSubscription already contains a 'level'
    // (e.g., { status: 'active', level: 'pro', ... })
    // Ensure subscription and subscription.level are valid before accessing FEATURE_MATRIX
    // Default to false if subscription or level are undefined, or feature not found
    return FEATURE_MATRIX[subscription?.level]?.includes(feature) || false;
  }

  async createOrUpdateCustomer(userId, email) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();

      // If customer already exists, return it
      if (userData?.stripeCustomerId) {
        return userData.stripeCustomerId;
      }

      // Create new customer
      const functions = getFunctions();
      const createStripeCustomer = httpsCallable(functions, 'createStripeCustomer');
      const result = await createStripeCustomer({ email });

      // Store customer ID
      await updateDoc(doc(db, 'users', userId), {
        stripeCustomerId: result.data.customerId,
        subscription: {
          status: 'active',
          level: 'basic',
          trial: true,
          trialEnds: new Date(Date.now() + (60 * 24 * 60 * 60 * 1000)) // 60 days
        }
      });

      return result.data.customerId;
    } catch (error) {
      console.error('Error in createOrUpdateCustomer:', error);
      throw error;
    }
  }

  /**
   * Start a trial subscription
   * @param {string} planId - The plan ID ('basic' or 'pro')
   * @param {string} email - The user's email address
   */  async startTrial(planId, email) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Starting trial for plan: ${planId}, email: ${email}`);
    }
    
    try {
      const auth = getAuth();
        // Parameter validation to prevent mistakes
      if (!planId || !['basic', 'pro'].includes(planId.toLowerCase())) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(`Invalid plan ID: ${planId}. Must be 'basic' or 'pro'`);
        }
        throw new Error('Invalid plan ID. Must be basic or pro.');
      }
        // Use the provided email or fall back to current user's email
      const userEmail = email || (auth.currentUser?.email);
      if (!userEmail || !userEmail.includes('@')) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(`Invalid email: ${userEmail}`);
        }
        throw new Error('Valid user email is required');
      }
      
      // Check authentication
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please log in and try again.');
      }
        // Force token refresh to ensure we have a valid token
      await auth.currentUser.getIdToken(true);
      if (process.env.NODE_ENV !== 'production') {
        console.log("Authentication token refreshed before starting trial");
      }
      
      // Get Firebase functions instance
      const functions = getFunctions();
      
      // Use correct function name
      const startTrialFunction = httpsCallable(functions, 'startTrialSubscription');
      
      // Make sure parameters are correctly ordered
      const params = {
        plan: planId.toLowerCase(), // Ensure lowercase for consistency
        email: userEmail      };
      
      if (process.env.NODE_ENV !== 'production') {
        console.log("Calling startTrialSubscription with params:", params);
      }
        // Make the call with the refreshed token
      const result = await startTrialFunction(params);
      
      if (process.env.NODE_ENV !== 'production') {
        console.log("Start trial result:", result.data);
      }
        return result.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error starting trial:", error);
      }
      throw error;
    }
  }
  
  async createCheckoutSession(userId, planId, isTrial, currentSubscriptionId, stripeCustomerId) {
    if (!planId) {
      console.error("[subscriptionService] createCheckoutSession: planId is required.");
      throw new Error("Plan ID is required to create a checkout session.");
    }
    
    try {
      // First, check for a Stripe customer ID if needed
      if (!stripeCustomerId) {
        try {
          // Optional: Check if we need to sync first (only if your app allows direct checkout without prior sync)
          // This is a light check - it only looks at local data, doesn't make a Firebase Function call
          const userSubData = await this.getUserSubscription(userId, { forceRefresh: false });
          
          // If there's no stripeCustomerId in the subscription data, we should sync first
          if (!userSubData?.subscription?.stripeCustomerId) {
            console.log("[subscriptionService] No Stripe customer ID found, syncing first...");
            // This will make the Firebase Function call to sync/create the customer
            const syncResult = await this.getUserSubscription(userId, { forceRefresh: true });
            
            // Try to get the customer ID after sync
            if (syncResult?.subscription?.stripeCustomerId) {
              stripeCustomerId = syncResult.subscription.stripeCustomerId;
            }
          } else {
            // Use the customer ID from the sync result
            stripeCustomerId = userSubData.subscription.stripeCustomerId;
          }
        } catch (syncError) {
          // Just log the error but continue, as createCheckoutSession may handle this itself
          console.warn("[subscriptionService] Pre-checkout sync warning:", syncError);
        }
      }
      
      const data = {
        planId: planId,
        isTrial: isTrial || false,
        currentSubscriptionId: currentSubscriptionId || null,
        stripeCustomerId: stripeCustomerId || null, // Pass the customer ID to the cloud function
      };
      if (process.env.NODE_ENV !== 'production') {
        console.log('[subscriptionService] Calling createCheckoutSession cloud function with:', data);
      }
      
      const functions = getFunctions();
      const createStripeCheckout = httpsCallable(functions, 'createCheckoutSession');
      
      const result = await createStripeCheckout(data);
      return result.data;
    } catch (error) {
      console.error('[subscriptionService] Error creating checkout session:', error);
      throw error;
    }
  }

  async handleSubscriptionChange(userId, status, priceId) {
    await updateDoc(doc(db, 'users', userId), {
      'subscription': {
        status,
        priceId,
        updatedAt: new Date().toISOString()
      }
    });
  }
  async createPortalSession(userId, customerId, returnUrl) {
    if (!userId || !customerId || !returnUrl) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("createPortalSession: Missing required parameters", { userId, customerId, returnUrl });
      }
      throw new Error("Missing required parameters for portal session");
    }    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log("Creating portal session with data:", { userId, customerId, returnUrl });
      }
      
      // Ensure we're passing a proper data object to the function
      const createPortalFunction = getFunctions();
      const createPortalTrigger = httpsCallable(createPortalFunction, 'createPortalSession');
      
      // Pass data as an object with named properties
      const result = await createPortalTrigger({
        customerId: customerId,
        returnUrl: returnUrl      });
      
      if (process.env.NODE_ENV !== 'production') {
        console.log("Portal session creation result:", result.data);
      }      return result.data;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error creating portal session:", error);
      }
      throw error;
    }
  }

  // CRITICAL FIX: Add updateSubscriptionInFirestore method
  async updateSubscriptionInFirestore(userId, subscriptionData) {
    if (!userId || !subscriptionData) {
      throw new Error('Missing required data for Firestore update');
    }
      try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Updating subscription in Firestore for user:', userId);
        console.log('With data:', subscriptionData);
      }
      
      // Get the level from the price ID if available
      let level = 'free';
      if (subscriptionData.priceId) {
        level = this.getPlanFromPriceId(subscriptionData.priceId);
      } else if (subscriptionData.level) {
        level = subscriptionData.level;
      }
      
      // Ensure we're writing the correct level determined from priceId
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscription: {
          ...subscriptionData,
          level: level,  // Override with the correct level
          updatedAt: new Date().toISOString()
        },
        selectedPlan: level,        updatedAt: new Date()
      });
      
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Successfully updated subscription in Firestore with level: ${level}`);
      }      return true;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error updating subscription in Firestore:', error);
      }
      throw error;
    }
  }

  async triggerTrialWelcomeEmail(planId, userName) {
    const functions = getFunctions();
    // Make sure the function name here matches the exported name in functions/index.js
    const callableFunction = httpsCallable(functions, 'triggerTrialWelcomeEmail'); 
    const auth = getAuth();    if (!auth.currentUser) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("[SubscriptionService] User not authenticated for triggering welcome email.");
      }
      // Optionally, you could throw an error or return a specific object
      // For now, just logging and returning null to prevent UI blockage
      return null;
    }    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[SubscriptionService] Calling triggerTrialWelcomeEmail with:`, { planId, userName, userEmail: auth.currentUser.email });
      }
      const result = await callableFunction({
        planId: planId, 
        userName: userName,        userEmail: auth.currentUser.email // Pass email explicitly
      });
      if (process.env.NODE_ENV !== 'production') {
        console.log('[SubscriptionService] Trigger trial welcome email result:', result.data);
      }      return result.data; // Contains { success: boolean, status: string, messageId?: string }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[SubscriptionService] Error triggering trial welcome email:', error);
      }
      // Rethrow or handle as appropriate for your app's error strategy
      // Returning null for now to avoid blocking UI for email issues
      return { success: false, error: error.message };
    }
  }
}

// Simple cache for preventing duplicate Stripe customer creation
const customerIdCache = new Map();
const customerCreationTimestamps = new Map(); // Add the missing Map declaration

/**
 * Ensures a user has a Stripe customer record, creating one if needed
 * With simple cooldown to prevent duplicates
 */
export async function ensureStripeCustomer(uid, email) {
  if (!uid) {
    console.error('No user ID provided to ensureStripeCustomer');
    return { success: false, error: 'No user ID provided' };
  }

  try {
    // Check client-side cache first (fast)
    if (customerIdCache.has(uid)) {
      console.log('Using cached Stripe customer ID for user:', uid);
      return { 
        success: true, 
        stripeCustomerId: customerIdCache.get(uid),
        fromCache: true 
      };
    }
    
    // Simple cooldown check (5 seconds)
    const now = Date.now();
    const lastCreationAttempt = customerCreationTimestamps.get(uid) || 0;
    if (now - lastCreationAttempt < 5000) { // 5 second cooldown
      console.log('Stripe customer creation on cooldown for user:', uid);
      return { 
        success: false, 
        error: 'Creation attempt too soon after previous attempt',
        retryAfter: 5 - Math.floor((now - lastCreationAttempt) / 1000)
      };
    }
    
    // Set timestamp for this attempt
    customerCreationTimestamps.set(uid, now);
    
    console.log('Ensuring Stripe customer record exists for user:', uid);
    
    // Check if we have a valid auth token before proceeding
    try {
      // Critical fix: Get current user's ID token
      const auth = getAuth();
      if (!auth.currentUser) {
        console.error('No authenticated user found when calling ensureStripeCustomer');
        return { success: false, error: 'User not authenticated' };
      }
      
      // Log before we call the Cloud Function
      console.log('[subscriptionService] Calling ensureStripeCustomer Cloud Function with:', {email});
      
      // First try with proper auth token
      const functions = getFunctions();
      const ensureCustomer = httpsCallable(functions, 'ensureStripeCustomer');
      const result = await ensureCustomer({ uid, email });
      
      console.log('ensureStripeCustomer result:', result.data);
      
      if (result.data.success && result.data.stripeCustomerId) {
        // Store in cache
        customerIdCache.set(uid, result.data.stripeCustomerId);
        
        // Update Firestore with the Stripe customer ID
        try {
          const userRef = doc(db, 'users', uid);
          await updateDoc(userRef, {
            stripeCustomerId: result.data.stripeCustomerId,
            lastUpdated: serverTimestamp()
          });
          console.log('Stripe customer ID saved to Firestore:', result.data.stripeCustomerId);
        } catch (firestoreError) {
          console.error('Failed to update Firestore with Stripe customer ID:', firestoreError);
          // Non-critical error - we'll retry next time
        }
      }
      
      return result.data;
    } catch (error) {
      // Log the full error
      console.error('[subscriptionService] Error calling ensureStripeCustomer Cloud Function:', error);
      
      // Try fallback to syncStripeCustomer which might have different auth requirements
      console.log('[subscriptionService] Attempting fallback: syncStripeCustomer');
      // Fix: Use the getUserSubscription function defined in this file rather than creating a new one
      return await getUserSubscription(uid, { forceRefresh: true, createCustomerIfNeeded: true });
    }
  } catch (error) {
    console.error('Error ensuring Stripe customer:', error);
    return { success: false, error: error.message };
  }
}

export const subscriptionService = new SubscriptionService();
export { SUBSCRIPTION_LEVELS, FEATURES };

/**
 * Gets user subscription status with proper trial validation
 * Note: This is the original getUserSubscription function - keep this one
 * and remove the duplicate below
 */
export async function getUserSubscription(uid, options = {}) {
  // Original getUserSubscription implementation
  if (!uid) {
    console.error('No user ID provided to getUserSubscription');
    return { status: 'inactive', level: 'free' };
  }

  try {
    // Enhanced error handling when checking Firestore
    try {
      // Get subscription data from Firestore
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Return the subscription data directly from Firestore
        return {
          status: userData.subscription?.status || 'inactive',
          level: userData.subscription?.level || 'free',
          availedTrials: userData.availedTrials || {},
          trialEnd: userData.subscription?.trialEnd || null,
          currentPeriodEnd: userData.subscription?.currentPeriodEnd || null
        };
      } else {
        console.log('No user document found, returning free tier');
        return { status: 'inactive', level: 'free' };
      }
    } catch (firestoreError) {
      console.error('Error checking user subscription in Firestore:', firestoreError);
      // Continue with cloud function check
    }

    // If Firestore check fails or user not found, fallback to cloud function
    const functions = getFunctions();
    const checkStripe = httpsCallable(functions, 'checkStripeSubscription');
    
    console.log('Checking with Stripe API');
    const result = await checkStripe({ userId: uid });
    console.log('Stripe API result:', result.data);

    if (result.data?.status === 'active') {
      return {
        status: 'active',
        level: subscriptionService.getPlanFromPriceId(result.data.priceId),
        currentPeriodEnd: result.data.currentPeriodEnd
      };
    } else {
      return { status: 'inactive', level: 'free' };
    }

  } catch (error) {
    console.error('Error in getUserSubscription:', error);
    return { status: 'inactive', level: 'free' };
  }
}